import { api } from "@repo/convex/api";
import { resolveCanonicalEntityPath } from "@repo/shared";
import { useRouterState } from "@tanstack/react-router";
import { useConvex } from "convex/react";
import { Folder, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useOrganization } from "@/contexts/organization-context";
import { useTabsState } from "@/contexts/tabs-context";
import {
  useProjectBySlug,
  useProjectEntities,
  useProjectFrameworkStructure,
  useProjectNavigationModel,
  useProjects,
} from "@/features/project/hooks";
import { resolveEntityDisplayIcon } from "@/features/project/lib/entity-icon-utils";
import {
  buildEntityDisplayMap,
  catalogHasEntityType,
  getCatalogEntityTypeDisplay,
  type ProjectFrameworkCatalog,
} from "@/features/project/lib/framework-registry";
import { makeProjectTabId } from "@/routing/core/locations";
import { normalizeSearchValue, scoreNameMatch } from "../lib/search";
import type { CommandPaletteResult, CommandPaletteScope } from "../types";

const MAX_RESULTS = 50;
const MAX_RECENT_PROJECT_RESULTS = 10;
const NON_TRACKABLE_CATALOG_ENTITY_TYPE_KEYS = new Set(["folder"]);
type RecentProjectEntity = {
  entityTypeKey: string;
  entityId: string;
};

type SearchableProjectEntity = {
  _entityTypeKey?: string;
  _id: string;
  slug: string;
  name: string;
};

function compareRank(a: CommandPaletteResult, b: CommandPaletteResult): number {
  if (a.score !== b.score) {
    return a.score - b.score;
  }

  const byLabel = a.label.localeCompare(b.label);
  if (byLabel !== 0) {
    return byLabel;
  }

  const byType = a.typeLabel.localeCompare(b.typeLabel);
  if (byType !== 0) {
    return byType;
  }

  return a.id.localeCompare(b.id);
}

function getActiveProjectSlug(pathname: string): string | null {
  if (!pathname.startsWith("/project/")) {
    return null;
  }

  const slug = pathname.split("/")[2];
  return slug && slug.length > 0 ? slug : null;
}

export function resultIdForEntityActivity(
  catalog: ProjectFrameworkCatalog | null | undefined,
  entityTypeKey: string,
  entityId: string
): string | null {
  return !NON_TRACKABLE_CATALOG_ENTITY_TYPE_KEYS.has(entityTypeKey) &&
    catalogHasEntityType(catalog, entityTypeKey)
    ? `${entityTypeKey}:${entityId}`
    : null;
}

export function toTypeLabel(
  catalog: ProjectFrameworkCatalog | null | undefined,
  displays: ReturnType<typeof buildEntityDisplayMap>,
  entityTypeKey: string
): string {
  const { name } = getCatalogEntityTypeDisplay(catalog, displays, entityTypeKey);
  if (name === "KPI") return name;
  return name
    .split(" ")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function resolveCommandPaletteIcon(
  entityTypeKey: string,
  iconKey: string | null | undefined
): LucideIcon {
  return resolveEntityDisplayIcon({ entityTypeKey, iconKey }) as LucideIcon;
}

export function resolveCommandPaletteEntityPath(input: {
  model: ReturnType<typeof useProjectNavigationModel>;
  entityId?: string | null;
  entityTypeKey: string;
  entitySlug: string;
}): string | null {
  if (!input.model) {
    return null;
  }

  return resolveCanonicalEntityPath({
    model: input.model,
    entityId: input.entityId,
    entityTypeKey: input.entityTypeKey,
    entitySlug: input.entitySlug,
  });
}

export function useCommandPaletteResults(query: string): {
  scope: CommandPaletteScope;
  isLoading: boolean;
  results: CommandPaletteResult[];
} {
  const convex = useConvex();
  const routerState = useRouterState();
  const { organizationId } = useOrganization();
  const { tabs } = useTabsState();

  const activeProjectSlug = getActiveProjectSlug(routerState.location.pathname);
  const scope: CommandPaletteScope = activeProjectSlug ? "project" : "home";
  const normalizedQuery = normalizeSearchValue(query);

  const activeProject = useProjectBySlug(organizationId, activeProjectSlug);
  const activeProjectId = activeProject?._id ?? null;

  const projects = useProjects(organizationId);
  const frameworkStructure = useProjectFrameworkStructure(activeProjectId);
  const projectEntities = useProjectEntities(activeProjectId);
  const navigationModel = useProjectNavigationModel(activeProjectId);
  const [recentProjectEntities, setRecentProjectEntities] = useState<
    RecentProjectEntity[] | null | undefined
  >(null);

  useEffect(() => {
    if (!(scope === "project" && activeProjectId && normalizedQuery.length === 0)) {
      setRecentProjectEntities(null);
      return;
    }

    let cancelled = false;
    setRecentProjectEntities(undefined);

    void convex
      .query(api.userEntityActivity.listRecentEntitiesForProject, {
        projectId: activeProjectId,
        limit: MAX_RECENT_PROJECT_RESULTS,
      })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setRecentProjectEntities(result);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        // Fallback safely when backend deployment does not yet include activity APIs.
        setRecentProjectEntities([]);
      });

    return () => {
      cancelled = true;
    };
  }, [activeProjectId, convex, normalizedQuery.length, scope]);

  const activeProjectNameFromTab = activeProjectSlug
    ? tabs.find((tab) => tab.tabId === makeProjectTabId(activeProjectSlug))?.name
    : null;

  const activeProjectName = activeProject?.name ?? activeProjectNameFromTab ?? "Project";
  const entityDisplays = useMemo(
    () => buildEntityDisplayMap(frameworkStructure ?? null),
    [frameworkStructure]
  );

  const isProjectLoading =
    scope === "project" &&
    (activeProject === undefined ||
      frameworkStructure === undefined ||
      projectEntities === undefined ||
      (normalizedQuery.length === 0 && recentProjectEntities === undefined));

  const isHomeLoading = scope === "home" && projects === undefined;
  const isLoading = isProjectLoading || isHomeLoading;

  const results = useMemo(() => {
    const candidateResults: CommandPaletteResult[] = [];

    if (scope === "home") {
      for (const project of projects ?? []) {
        const score = scoreNameMatch(project.name, query);
        if (score === null) {
          continue;
        }

        candidateResults.push({
          id: `project:${project.slug}`,
          label: project.name,
          typeLabel: "Project",
          icon: Folder,
          action: {
            type: "openProject",
            projectSlug: project.slug,
            projectName: project.name,
          },
          score,
        });
      }
    }

    if (scope === "project" && activeProjectSlug && activeProject) {
      for (const entity of (projectEntities ?? []) as SearchableProjectEntity[]) {
        const entityTypeKey = entity._entityTypeKey;
        if (!entityTypeKey) {
          continue;
        }

        const score = scoreNameMatch(entity.name, query);
        if (score === null) {
          continue;
        }

        const path = resolveCommandPaletteEntityPath({
          model: navigationModel,
          entityId: String(entity._id),
          entityTypeKey,
          entitySlug: entity.slug,
        });

        if (!path) {
          continue;
        }

        candidateResults.push({
          id: `${entityTypeKey}:${String(entity._id)}`,
          label: entity.name,
          typeLabel: toTypeLabel(
            frameworkStructure?.compiledCatalog,
            entityDisplays,
            entityTypeKey
          ),
          icon: resolveCommandPaletteIcon(
            entityTypeKey,
            entityDisplays.get(entityTypeKey)?.iconKey
          ),
          action: {
            type: "navigateProjectPath",
            projectSlug: activeProjectSlug,
            projectName: activeProjectName,
            path,
          },
          score,
        });
      }
    }

    if (scope === "project" && normalizedQuery.length === 0) {
      const sortedCandidates = [...candidateResults].sort(compareRank);
      const resultById = new Map<string, CommandPaletteResult>();
      for (const candidate of candidateResults) {
        resultById.set(candidate.id, candidate);
      }

      const recentResults: CommandPaletteResult[] = [];
      const recentIds = new Set<string>();
      for (const activity of recentProjectEntities ?? []) {
        const resultId = resultIdForEntityActivity(
          frameworkStructure?.compiledCatalog,
          activity.entityTypeKey,
          activity.entityId
        );
        if (!resultId || recentIds.has(resultId)) {
          continue;
        }
        recentIds.add(resultId);

        const match = resultById.get(resultId);
        if (!match) {
          continue;
        }

        recentResults.push(match);
        if (recentResults.length >= MAX_RECENT_PROJECT_RESULTS) {
          break;
        }
      }

      if (recentResults.length < MAX_RECENT_PROJECT_RESULTS) {
        for (const candidate of sortedCandidates) {
          if (recentIds.has(candidate.id)) {
            continue;
          }
          recentResults.push(candidate);
          if (recentResults.length >= MAX_RECENT_PROJECT_RESULTS) {
            break;
          }
        }
      }

      return recentResults.slice(0, MAX_RECENT_PROJECT_RESULTS);
    }

    return [...candidateResults].sort(compareRank).slice(0, MAX_RESULTS);
  }, [
    activeProject,
    activeProjectName,
    activeProjectSlug,
    entityDisplays,
    frameworkStructure?.compiledCatalog,
    navigationModel,
    projectEntities,
    projects,
    query,
    normalizedQuery,
    recentProjectEntities,
    scope,
  ]);

  return {
    scope,
    isLoading,
    results,
  };
}
